/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import config from '../../../../share/config';
import session from '../../../../share/session';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';

/** レスポンス定数定義 */
const RESULT_NORMAL = 'normal';
const RESULT_UNSUITABLE = 'unsuitable';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  CONFIRMATION: 'confirmation',
  WATCH: 'watch',
  SUCCESS: 'success',
  FAIL: 'fail',
  CONNECTION_FAILED: 'connectionFailed'
};

export default {
  name: 'requirement031',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      isDisabled: false,
      modeList: [],
      selectedMode: '',
      errorAction: '',
      systemId: '',
      // ダイアログフラグ
      modal_requirement_031: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAIL:
        case PHASE.CONNECTION_FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        case PHASE.CONFIRMATION:
        case PHASE.WATCH:
        default:
          return 'secondary';
      }
    }
  },

  async mounted() {
    this.init();
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      // モード一覧作成
      this.modeList = [
        { value: "front", text: this.$t(`individual.label_mode_front`) },
        { value: "rear", text: this.$t(`individual.label_mode_rear`) },
        { value: "both", text: this.$t(`individual.label_mode_both`) }
      ];
      this.modal_requirement_031 = true;
      //EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    //----- htmlイベントメソッド -----------------------------------------------//

    /** 待機画面のOKクリックイベント */
    clickShowConfirmation() {
      if (this.selectedMode !== '') {
        this.phase = PHASE.CONFIRMATION;
      }
    },

    /** 
     * 終了処理クリックイベント 
     * @param {string} errorAction
     *    (transition : exitApiでエラー発生時、共通エラーに遷移する）
     *    (ignore : exitApiでエラー発生時、共通エラーに遷移しない）
     */
    async clickExit(errorAction) {
      this.errorAction = errorAction;
      await this.exitPostGet();
    },

    /** 確認画面のOKクリックイベント */
    async clickExecute() {
      this.phase = PHASE.WATCH;
      await this.executePostGet();
    },

    //----- close操作メソッド -----------------------------------------------//

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_031 = false;
    },

    //----- バックエンドAPI実行メソッド -----------------------------------------------//

    /**
     * ルーチン処理（要求/取得）メソッド
    */
    async executePostGet() {

      try {
        this.isLoading = true;
        this.isDisabled = true;

        // API通信エラー発生時、共通エラー表示
        this.errorAction = 'transition';

        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute',
            mode: this.selectedMode,
          },
          // エラー発生のコールバックを実装
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        //正常
        if (response) {
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            switch (response.data.user_data.result) {
              case RESULT_NORMAL:
                this.phase = PHASE.SUCCESS;
                break;
              case RESULT_UNSUITABLE:
                this.phase = PHASE.FAIL;
                break;
              default:
                this.phase = PHASE.CONNECTION_FAILED;
                break;
            }
          }
          //ステータス：異常
          else {
            this.phase = PHASE.CONNECTION_FAILED;
          }
        }
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * 終了処理メソッド
     */
    async exitPostGet() {

      try {
        this.isLoading = true;
        this.isDisabled = true;

        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit',
          },
          // エラー発生のコールバックを実装
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        //エラー遷移なしの場合：response判定せず終了
        if (this.errorAction !== 'transition') {
          this.closeWizard();
          return;
        }

        //エラー遷移ありの場合：response正常の場合は終了
        if (response) {
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            this.closeWizard();
            return;
          }
        }
        //上記で終了しない場合はresponse異常となり共通エラー表示
        this.phase = PHASE.CONNECTION_FAILED;

      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    //----- 異常コールバックメソッド -----------------------------------------------//

    /**
     * API 通信エラー発生のコールバック用
    */
    showUnexpectedError() {
      //エラー遷移あり：共通エラー表示
      if (this.errorAction === 'transition') {
        this.phase = PHASE.CONNECTION_FAILED;
      }
      //それ以外：終了
      else{
        this.closeWizard();
      }
    },

    /**
     * API 通信共通バリデーションエラー発生のコールバック
     * @param {object} result バリデーション結果
   */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

  },
};